import { useMemo } from 'react';
import { Save } from 'lucide-react';

import ImageInput from 'components/storefront/StorefrontConfigEditor/components/ImageInput';
import ProductTable from 'components/storefront/StorefrontConfigEditor/components/ProductTable';
import { Button } from 'shadcn/components/ui/Button';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Textarea } from 'shadcn/components/ui/Textarea';

/**
 * Form for the carousel editor.
 *
 * @param {Object} data - Non-configurable carousel data.
 * @param {Object} formData - Configurable carousel data.
 * @param {Function} handleBackgroundImageClear - Callback function to clear the background image.
 * @param {Function} handleBackgroundImageUpload - Callback function to upload the background image.
 * @param {string} backgroundImageError - Background image error.
 * @param {Function} handleChange - Callback function to handle input changes.
 * @param {Function} handleImageClear - Callback function to handle image clear.
 * @param {Function} handleImageUpload - Callback function to handle image upload.
 * @param {Function} handleSave - Callback function to handle form submission.
 * @param {Object} imageError - Image error.
 * @param {number} index - Index of the carousel.
 * @param {boolean} isSaving - Whether the carousel is being saved.
 * @param {Object} productTableHandlers - Product table handlers.
 * @param {Object} sellableItems - Sellable items.
 */
export default function CarouselEditorForm({
    backgroundImageError,
    data,
    formData,
    handleBackgroundImageClear,
    handleBackgroundImageUpload,
    handleChange,
    handleImageClear,
    handleImageUpload,
    handleSave,
    imageError,
    index,
    isSaving,
    productTableHandlers,
    sellableItems,
}) {
    const {
        handleAddProduct,
        handleAssignProductRank,
        handleDecrementProductRank,
        handleIncrementProductRank,
        handleRemoveAllProducts,
        handleRemoveSelectedProducts,
    } = productTableHandlers;

    const enrichedProducts = useMemo(
        () =>
            formData.items.map((item, i) => ({
                ...item,
                rank: i + 1,
                valid: sellableItems.some(
                    // The carousels endpoint returns items with a `sellableItemID` property,
                    // that matches `id` property for items returned from the sellable items endpoint.
                    (sellableItem) => sellableItem.id === item.sellableItemID || sellableItem.id === item.id,
                ),
            })),
        [formData.items, sellableItems],
    );

    const productOptions = useMemo(() => {
        const existingProducts = new Set(formData.items.map((item) => item.id));

        return sellableItems.reduce((acc, sellableItem) => {
            if (!existingProducts.has(sellableItem.id)) {
                acc.push({
                    value: sellableItem.externalSku,
                    label: `${sellableItem.externalSku}: ${sellableItem.name}`,
                });
            }
            return acc;
        }, []);
    }, [sellableItems, formData.items]);

    return (
        <form className="flex flex-col gap-6" onSubmit={handleSave}>
            {['PRODUCT_CAROUSEL_2', 'PRODUCT_CAROUSEL_3'].includes(data.displayType) && (
                <ImageInput
                    className="[&_img]:max-h-20"
                    id={`image-${data.id}-${index}`}
                    imageUrl={formData.imageUrl}
                    label="Image"
                    onClearImage={handleImageClear}
                    onImageUpload={handleImageUpload}
                    error={imageError}
                />
            )}
            <div className="flex flex-col gap-2">
                <Label htmlFor={`eyebrow-${data.id}-${index}`}>Eyebrow</Label>
                <Input
                    type="text"
                    id={`eyebrow-${data.id}-${index}`}
                    name="eyebrow"
                    value={formData.eyebrow ?? ''}
                    placeholder="Enter eyebrow"
                    onChange={handleChange}
                />
            </div>
            <div className="flex flex-col gap-2">
                <Label htmlFor={`name-${data.id}-${index}`}>Name</Label>
                <Input
                    type="text"
                    id={`name-${data.id}-${index}`}
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter name"
                    required
                />
            </div>
            <div className="flex flex-col gap-2">
                <Label htmlFor={`description-${data.id}-${index}`}>Description</Label>
                <Textarea
                    type="text"
                    id={`description-${data.id}-${index}`}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter description"
                />
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                    <Label htmlFor={`link-url-${data.id}-${index}`}>Link URL</Label>
                    <Input
                        type="text"
                        id={`link-url-${data.id}-${index}`}
                        name="linkUrl"
                        value={formData.linkUrl}
                        placeholder="Enter link URL"
                        onChange={handleChange}
                    />
                </div>
                <div className="flex flex-col gap-2">
                    <Label htmlFor={`link-text-${data.id}-${index}`}>Link Text</Label>
                    <Input
                        type="text"
                        id={`link-text-${data.id}-${index}`}
                        name="linkText"
                        value={formData.linkText}
                        placeholder="Enter link text"
                        onChange={handleChange}
                    />
                </div>
            </div>
            {data.displayType === 'PRODUCT_CAROUSEL_1' && (
                <ImageInput
                    className="[&_img]:max-h-20"
                    id={`background-image-${data.id}-${index}`}
                    imageUrl={formData.backgroundImageUrl}
                    label="Background image"
                    onClearImage={handleBackgroundImageClear}
                    onImageUpload={handleBackgroundImageUpload}
                    error={backgroundImageError}
                />
            )}
            <ProductTable
                carouselID={data.id}
                dialogTitle={formData.name ? `${formData.name} (id: ${data.id})` : `Products for ${data.id}`}
                loading={false}
                onAddRow={handleAddProduct}
                onMoveRowDown={handleDecrementProductRank}
                onMoveRowUp={handleIncrementProductRank}
                onMoveRowToIndex={handleAssignProductRank}
                onRemoveAllRows={handleRemoveAllProducts}
                onRemoveRow={(externalSku) => handleRemoveSelectedProducts([externalSku])}
                onRemoveSelectedRows={handleRemoveSelectedProducts}
                options={productOptions}
                rows={enrichedProducts}
                isDialog={false}
                initialRowsPerPage="5"
            />

            <Button className="self-end w-content" disabled={isSaving}>
                <Save />
                Save changes
            </Button>
        </form>
    );
}
